// useViolationTracking.ts
import { useEffect, useRef } from 'react';
import { ViolationLog,  ViolationEventType} from 'types';

export const useViolationTracking = (
    isTrackingActive: boolean,
    pcode: string,
    currentTrialNumber: string | number
) => {
    const hasMovedMouse = useRef(false);
    const violationLogs = useRef<ViolationLog[]>([]);

    // 1. The mandatory live mirror for the dynamic trial number
    const trialRef = useRef(currentTrialNumber);
    const pcodeRef = useRef(pcode)

    // 2. Keep the mirror perfectly synced as the participant advances trials
    useEffect(() => {
        trialRef.current = currentTrialNumber;
    }, [currentTrialNumber]);

    useEffect(() => {
        pcodeRef.current = pcode;
    }, [pcode]);

    useEffect(() => {
        if (!isTrackingActive) return;

        const logEvent = (eventName: ViolationEventType) => {
            violationLogs.current.push({
                pcode: pcodeRef.current,
                trialNumber: trialRef.current, // 3. Read straight from the mirror!
                event: eventName,
                time: new Date().toLocaleTimeString() // Date.now()
            });
        };

        // Define the handlers
        const handleBlur = () => logEvent('window_left');
        const handleFocus = () => logEvent('window_returned');
        const handleMouseLeave = () => logEvent('mouse_left_page');
        const handleMouseEnter = () => logEvent('mouse_returned');
        const handleVisibilityChange = () => {
            if (document.hidden) {
                logEvent('tab_hidden');
            } else {
                logEvent('tab_visible');
            }
        };

        // 1. Prevent & log text copying (Stops them from feeding stimuli to AI)
        const handleCopy = (e: ClipboardEvent) => {
            e.preventDefault(); // Physically blocks the copy action
            logEvent('text_copied');
        };

        // 2. Prevent & log text pasting (Stops them pasting AI answers)
        const handlePaste = (e: ClipboardEvent) => {
            // Only block/log if it's not a whitelisted field (optional)
            e.preventDefault(); // Physically blocks the paste action
            logEvent('text_pasted');
        };

        // 3. Block right-click context menu (Forces manual typing, slows down AI usage)
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            logEvent('context_menu_open');
        };

        // 4. Track window resizing (Detects tiling windows side-by-side with AI)
        const handleResize = () => {
            logEvent('window_resized');
        };

        // 5. Detect clicks that arrive without any prior mouse movement (bot/script signal)
        const handleMouseMove = () => { hasMovedMouse.current = true; };
        const handleTeleportClick = (e: MouseEvent) => {
            if (!hasMovedMouse.current) {
                logEvent('teleportation_detected');
            }
        };



        // Attach all listeners
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);
        document.addEventListener('mouseleave', handleMouseLeave);
        document.addEventListener('mouseenter', handleMouseEnter);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('copy', handleCopy);
        document.addEventListener('paste', handlePaste);
        document.addEventListener('contextmenu', handleContextMenu);
        window.addEventListener('resize', handleResize);
        document.addEventListener('mousemove', handleMouseMove, { once: true });
        document.addEventListener('click', handleTeleportClick as EventListener);

        // CLEANUP: Remove all listeners when component unmounts
        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('mouseleave', handleMouseLeave);
            document.removeEventListener('mouseenter', handleMouseEnter);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('paste', handlePaste);
            document.removeEventListener('contextmenu', handleContextMenu);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('click', handleTeleportClick as EventListener);
        };
    }, [isTrackingActive, pcode]); // Re-run effect if tracking status changes

    // Return a function that allows the main component to retrieve the logs
    const getLogs = () => violationLogs.current;

    return { getLogs };
};
